const STORAGE_KEY = 'yg:comparison:v1';
const STORAGE_VERSION = 1;
const MAX_PRODUCTS = 10;
const SPECIFICATION_ALIASES = {
  panel_for_filter: 'panel',
  refreshrate: 'refresh_rate',
  resolution_for_filter: 'resolution'
};
const HIDDEN_SPECIFICATION_KEYS = new Set(['schema_product', 'user_manual_page', 'user_manuals']);
const SPECIFICATION_ORDER = [
  'screen_size',
  'panel',
  'resolution',
  'refresh_rate',
  'response_time',
  'aspect_ratio',
  'brightness',
  'contrast_ratio',
  'color_gamut',
  'color_gamut_volume',
  'hdr_support',
  'low_blue_light',
  'ports',
  'adjustment',
  'power',
  'product_size',
  'product_weight'
];
const SPECIFICATION_LABELS = {
  adjustment: 'Adjustment',
  aspect_ratio: 'Aspect Ratio',
  brightness: 'Brightness',
  color_gamut: 'Color Gamut',
  color_gamut_volume: 'Color Gamut Volume',
  contrast_ratio: 'Contrast Ratio',
  hdr_support: 'HDR Support',
  low_blue_light: 'Low Blue Light',
  panel: 'Panel',
  ports: 'Ports',
  power: 'Power',
  product_size: 'Product Size',
  product_weight: 'Product Weight',
  refresh_rate: 'Refresh Rate',
  resolution: 'Resolution',
  response_time: 'Response Time',
  screen_size: 'Screen Size'
};

function getUnqualifiedSpecificationKey(key) {
  return String(key || '').split('.').pop();
}

function isHiddenSpecificationKey(key) {
  return HIDDEN_SPECIFICATION_KEYS.has(getUnqualifiedSpecificationKey(key));
}

function getSafeUrl(value, allowExternal = false) {
  try {
    const url = new URL(String(value || ''), window.location.origin);
    const isHttpUrl = url.protocol === 'http:' || url.protocol === 'https:';
    const isAllowedOrigin = allowExternal || url.origin === window.location.origin;

    return isHttpUrl && isAllowedOrigin ? url.href : '';
  } catch (error) {
    return '';
  }
}

function getPlainText(value) {
  const stringValue = String(value || '');
  if (!stringValue.includes('<')) return stringValue.trim();

  if (typeof DOMParser === 'undefined') {
    return stringValue.replace(/<[^>]*>/g, '').trim();
  }

  return new DOMParser().parseFromString(stringValue, 'text/html').body.textContent.trim();
}

function parseJsonObject(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return {};

  try {
    const parsedValue = JSON.parse(value);
    return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue) ? parsedValue : {};
  } catch (error) {
    return {};
  }
}

function getConfiguredSpecifications() {
  const structureElement = document.getElementById('comparison-structure');
  if (!structureElement) return [];

  try {
    const structure = JSON.parse(structureElement.textContent || '[]');
    if (!Array.isArray(structure)) return [];

    return structure
      .map((item) => {
        const key = String(item?.key || '').trim();
        const label = String(item?.label || '').trim();
        return key && label ? { key, label } : null;
      })
      .filter((item) => item && !isHiddenSpecificationKey(item.key));
  } catch (error) {
    return [];
  }
}

function normalizeSpecificationValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeSpecificationValue(item)).filter((item) => hasSpecificationValue(item));
  }

  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean' || typeof value === 'number') return value;
  if (typeof value === 'object') return getPlainText(JSON.stringify(value));

  return getPlainText(value);
}

function hasSpecificationValue(value) {
  if (Array.isArray(value)) return value.some((item) => hasSpecificationValue(item));
  if (value === null || value === undefined) return false;
  if (typeof value !== 'string') return true;

  const normalizedValue = value.trim();
  return normalizedValue !== '' && normalizedValue !== '-' && normalizedValue !== '\u2013' && normalizedValue !== '\u2014';
}

function normalizeSpecifications(value) {
  const source = parseJsonObject(value);
  const specifications = {};

  Object.entries(source).forEach(([key, rawValue]) => {
    const unqualifiedKey = getUnqualifiedSpecificationKey(key);
    if (isHiddenSpecificationKey(key) || (key === unqualifiedKey && SPECIFICATION_ALIASES[key])) return;

    const normalizedValue = normalizeSpecificationValue(rawValue);
    if (hasSpecificationValue(normalizedValue)) specifications[key] = normalizedValue;
  });

  Object.entries(SPECIFICATION_ALIASES).forEach(([alias, canonicalKey]) => {
    if (hasSpecificationValue(specifications[canonicalKey])) return;

    const normalizedValue = normalizeSpecificationValue(source[alias]);
    if (hasSpecificationValue(normalizedValue)) specifications[canonicalKey] = normalizedValue;
  });

  return specifications;
}

function getSpecificationKeys(items) {
  const availableKeys = new Set();

  items.forEach((product) => {
    Object.keys(product.specifications).forEach((key) => availableKeys.add(key));
  });

  const orderedKeys = SPECIFICATION_ORDER.filter((key) => availableKeys.delete(key));

  return [...orderedKeys, ...Array.from(availableKeys).sort()].filter((key) => {
    return items.some((product) => hasSpecificationValue(product.specifications[key]));
  });
}

function getSpecificationValue(specifications, key) {
  const unqualifiedKey = getUnqualifiedSpecificationKey(key);
  const namespaceSeparatorIndex = key.indexOf('.');
  const namespace = namespaceSeparatorIndex > 0 ? key.slice(0, namespaceSeparatorIndex) : '';
  const canonicalKey = SPECIFICATION_ALIASES[unqualifiedKey];
  const candidateKeys = [key];

  if (unqualifiedKey !== key) candidateKeys.push(unqualifiedKey);
  if (canonicalKey) {
    if (namespace) candidateKeys.push(`${namespace}.${canonicalKey}`);
    candidateKeys.push(canonicalKey);
  }

  const matchedKey = candidateKeys.find((candidateKey) => hasSpecificationValue(specifications[candidateKey]));
  return matchedKey ? specifications[matchedKey] : undefined;
}

function getSpecificationDefinitions(items) {
  const configuredSpecifications = getConfiguredSpecifications();

  if (configuredSpecifications.length > 0) {
    return configuredSpecifications.filter((specification) => {
      return items.some((product) => hasSpecificationValue(getSpecificationValue(product.specifications, specification.key)));
    });
  }

  return getSpecificationKeys(items).map((key) => ({
    key,
    label: getSpecificationLabel(key)
  }));
}

function getSpecificationLabel(key) {
  if (SPECIFICATION_LABELS[key]) return SPECIFICATION_LABELS[key];

  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getSpecificationDisplayValue(value) {
  if (!hasSpecificationValue(value)) return '\u2014';
  if (Array.isArray(value)) {
    return value
      .filter((item) => hasSpecificationValue(item))
      .map((item) => getSpecificationDisplayValue(item))
      .join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function normalizeProduct(product) {
  if (!product || product.id === undefined || product.id === null) return null;

  const id = String(product.id).trim();
  if (!id) return null;

  return {
    id,
    handle: String(product.handle || ''),
    url: String(product.url || ''),
    title: String(product.title || ''),
    image: {
      src: String(product.image?.src || ''),
      alt: String(product.image?.alt || product.title || '')
    },
    price: {
      amount: Number.isFinite(Number(product.price?.amount)) ? Number(product.price.amount) : 0,
      formatted: getPlainText(product.price?.formatted)
    },
    specifications: normalizeSpecifications(product.specifications)
  };
}

function normalizeItems(items) {
  if (!Array.isArray(items)) return [];

  const seenIds = new Set();

  return items.reduce((products, item) => {
    const product = normalizeProduct(item);

    if (!product || seenIds.has(product.id) || products.length >= MAX_PRODUCTS) return products;

    seenIds.add(product.id);
    products.push(product);
    return products;
  }, []);
}

class YGComparisonStore {
  constructor() {
    this.items = this.read();

    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;

      this.items = this.read();
      this.emit();
    });
  }

  read() {
    try {
      const storedValue = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      if (!storedValue || storedValue.version !== STORAGE_VERSION) return [];

      return normalizeItems(storedValue.items);
    } catch (error) {
      return [];
    }
  }

  getItems() {
    return this.items.slice();
  }

  has(productId) {
    return this.items.some((item) => item.id === String(productId));
  }

  add(product) {
    const normalizedProduct = normalizeProduct(product);
    if (!normalizedProduct) return { added: false, reason: 'invalid' };
    if (this.has(normalizedProduct.id)) return { added: false, reason: 'exists' };
    if (this.items.length >= MAX_PRODUCTS) return { added: false, reason: 'limit' };

    this.save([...this.items, normalizedProduct]);
    return { added: true };
  }

  update(product) {
    const normalizedProduct = normalizeProduct(product);
    if (!normalizedProduct) return;

    const productIndex = this.items.findIndex((item) => item.id === normalizedProduct.id);
    if (productIndex === -1 || JSON.stringify(this.items[productIndex]) === JSON.stringify(normalizedProduct)) return;

    const nextItems = this.getItems();
    nextItems[productIndex] = normalizedProduct;
    this.save(nextItems);
  }

  remove(productId) {
    const nextItems = this.items.filter((item) => item.id !== String(productId));
    if (nextItems.length === this.items.length) return;

    this.save(nextItems);
  }

  clear() {
    if (this.items.length === 0) return;
    this.save([]);
  }

  save(items) {
    this.items = normalizeItems(items);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: STORAGE_VERSION,
        items: this.items
      }));
    } catch (error) {
      // Keep the comparison working for the current page when storage is unavailable.
    }

    this.emit();
  }

  emit() {
    document.dispatchEvent(new CustomEvent('yg:comparison:change', {
      detail: { items: this.getItems() }
    }));
  }
}

const comparisonStore = new YGComparisonStore();

class YGCompareToggle extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-yg-compare-checkbox]');
    if (!this.input) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.input.addEventListener('change', () => this.handleChange(), { signal });
    document.addEventListener('yg:comparison:change', () => this.sync(), { signal });
    this.sync(true);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  getProduct() {
    const data = this.input.dataset;

    return {
      id: data.productId,
      handle: data.productHandle,
      url: data.productUrl,
      title: data.productTitle,
      image: {
        src: data.productImage,
        alt: data.productImageAlt
      },
      price: {
        amount: Number(data.productPrice || 0),
        formatted: data.productPriceFormatted
      },
      specifications: data.productSpecifications
    };
  }

  handleChange() {
    const productId = this.input.dataset.productId;

    if (!this.input.checked) {
      comparisonStore.remove(productId);
      return;
    }

    const result = comparisonStore.add(this.getProduct());

    if (result.reason === 'limit') {
      this.input.checked = false;
      document.dispatchEvent(new CustomEvent('yg:comparison:limit'));
    }
  }

  sync(refreshProduct = false) {
    const isSelected = comparisonStore.has(this.input.dataset.productId);
    this.input.checked = isSelected;

    if (isSelected && refreshProduct) comparisonStore.update(this.getProduct());
  }
}

class YGComparisonBar extends HTMLElement {
  connectedCallback() {
    this.expanded = false;
    this.limitTimer = null;
    this.abortController = new AbortController();

    this.trigger = this.querySelector('[data-yg-comparison-trigger]');
    this.panel = this.querySelector('[data-yg-comparison-panel]');
    this.count = this.querySelector('[data-yg-comparison-count]');
    this.productsContainer = this.querySelector('[data-yg-comparison-products]');
    this.productTemplate = this.querySelector('[data-yg-comparison-product-template]');
    this.compareLink = this.querySelector('[data-yg-comparison-link]');
    this.clearButton = this.querySelector('[data-yg-comparison-clear]');
    this.status = this.querySelector('[data-yg-comparison-status]');

    const { signal } = this.abortController;

    this.trigger?.addEventListener('click', () => this.togglePanel(), { signal });
    this.clearButton?.addEventListener('click', () => comparisonStore.clear(), { signal });
    this.productsContainer?.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-yg-comparison-remove]');
      if (removeButton) comparisonStore.remove(removeButton.dataset.productId);
    }, { signal });
    document.addEventListener('yg:comparison:change', () => this.render(), { signal });
    document.addEventListener('yg:comparison:limit', () => this.showLimitMessage(), { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.expanded) this.setExpanded(false);
    }, { signal });

    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.limitTimer);
  }

  togglePanel() {
    this.setExpanded(!this.expanded);
  }

  setExpanded(expanded) {
    this.expanded = Boolean(expanded);
    this.classList.toggle('is-expanded', this.expanded);
    this.trigger?.setAttribute('aria-expanded', String(this.expanded));

    if (this.panel) this.panel.hidden = !this.expanded;
  }

  showLimitMessage() {
    if (!this.status) return;

    this.status.hidden = false;
    window.clearTimeout(this.limitTimer);
    this.limitTimer = window.setTimeout(() => {
      this.status.hidden = true;
    }, 3500);
  }

  createProductCard(product) {
    const card = this.productTemplate.content.firstElementChild.cloneNode(true);
    const image = card.querySelector('[data-yg-product-image]');
    const removeButton = card.querySelector('[data-yg-comparison-remove]');

    const productUrl = getSafeUrl(product.url);

    card.querySelectorAll('[data-yg-product-link]').forEach((link) => {
      if (productUrl) link.href = productUrl;
    });

    const imageUrl = getSafeUrl(product.image.src, true);

    if (imageUrl) {
      image.src = imageUrl;
      image.alt = product.image.alt;
    } else {
      image.closest('[data-yg-product-media]').hidden = true;
    }

    card.querySelector('[data-yg-product-title]').textContent = product.title;
    card.querySelector('[data-yg-product-price]').textContent = product.price.formatted;
    removeButton.dataset.productId = product.id;
    removeButton.setAttribute('aria-label', `Remove ${product.title} from comparison`);
    return card;
  }

  render() {
    const items = comparisonStore.getItems();

    this.hidden = items.length === 0;
    if (items.length === 0) this.setExpanded(false);
    if (this.count) this.count.textContent = String(items.length);
    this.dataset.count = String(items.length);

    this.productsContainer?.style.setProperty('--yg-comparison-bar-count', items.length);
    this.productsContainer?.replaceChildren(...items.map((product) => this.createProductCard(product)));
  }
}

class YGComparisonTable extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.emptyState = this.querySelector('[data-yg-comparison-empty]');
    this.content = this.querySelector('[data-yg-comparison-content]');
    this.scrollContainer = this.querySelector('.yg-comparison-table__scroll');
    this.matrix = this.querySelector('[data-yg-comparison-matrix]');
    this.productGrid = this.querySelector('[data-yg-comparison-table-products]');
    this.parameters = this.querySelector('[data-yg-comparison-parameters]');
    this.productTemplate = this.querySelector('[data-yg-comparison-table-product-template]');
    this.clearButton = this.querySelector('[data-yg-comparison-table-clear]');
    this.count = this.querySelector('[data-yg-comparison-table-count]');
    this.scrollStartPosition = this.scrollContainer?.scrollLeft || 0;

    const { signal } = this.abortController;

    this.clearButton?.addEventListener('click', () => comparisonStore.clear(), { signal });
    this.productGrid?.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-yg-comparison-remove]');
      if (removeButton) comparisonStore.remove(removeButton.dataset.productId);
    }, { signal });
    document.addEventListener('yg:comparison:change', () => this.render(), { signal });
    this.scrollContainer?.addEventListener('scroll', () => this.updateStickyDivider(), {
      passive: true,
      signal
    });

    this.render();
    this.updateStickyDivider();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  updateStickyDivider() {
    const scrollDistance = Math.abs((this.scrollContainer?.scrollLeft || 0) - this.scrollStartPosition);
    this.classList.toggle('is-parameter-column-stuck', scrollDistance > 1);
  }

  createProductCard(product) {
    const card = this.productTemplate.content.firstElementChild.cloneNode(true);
    const image = card.querySelector('[data-yg-product-image]');
    const removeButton = card.querySelector('[data-yg-comparison-remove]');

    const productUrl = getSafeUrl(product.url);

    card.querySelectorAll('[data-yg-product-link]').forEach((link) => {
      if (productUrl) link.href = productUrl;
    });

    const imageUrl = getSafeUrl(product.image.src, true);

    if (imageUrl) {
      image.src = imageUrl;
      image.alt = product.image.alt;
    } else {
      image.closest('[data-yg-product-media]').hidden = true;
    }

    card.querySelector('[data-yg-product-title]').textContent = product.title;
    card.querySelector('[data-yg-product-price]').textContent = product.price.formatted;
    removeButton.dataset.productId = product.id;
    removeButton.setAttribute('aria-label', `Remove ${product.title} from comparison`);
    return card;
  }

  renderSpecifications(items) {
    if (!this.parameters) return;

    const specificationDefinitions = getSpecificationDefinitions(items);
    this.parameters.hidden = specificationDefinitions.length === 0;

    const rows = specificationDefinitions.map((specification) => {
      const row = document.createElement('div');
      row.className = 'yg-comparison-table__parameter-row';
      row.setAttribute('role', 'row');

      const label = document.createElement('div');
      label.className = 'yg-comparison-table__parameter-label';
      label.setAttribute('role', 'rowheader');
      label.textContent = specification.label;
      row.append(label);

      items.forEach((product) => {
        const value = document.createElement('div');
        value.className = 'yg-comparison-table__parameter-value';
        value.setAttribute('role', 'cell');
        value.textContent = getSpecificationDisplayValue(getSpecificationValue(product.specifications, specification.key));
        row.append(value);
      });

      return row;
    });

    this.parameters.replaceChildren(...rows);
  }

  render() {
    const items = comparisonStore.getItems();
    const hasProducts = items.length > 0;

    if (this.emptyState) this.emptyState.hidden = hasProducts;
    if (this.content) this.content.hidden = !hasProducts;
    if (this.clearButton) this.clearButton.hidden = !hasProducts;
    if (this.count) this.count.textContent = String(items.length);
    if (!hasProducts) {
      this.productGrid?.replaceChildren();
      this.parameters?.replaceChildren();
      return;
    }

    this.matrix?.style.setProperty('--yg-comparison-count', items.length);
    this.matrix?.style.setProperty('--yg-comparison-min-width', `${180 + items.length * 260}px`);
    this.matrix?.style.setProperty('--yg-comparison-min-width-mobile', `${120 + items.length * 252}px`);
    if (this.matrix) this.matrix.dataset.count = String(items.length);
    this.productGrid.replaceChildren(...items.map((product) => this.createProductCard(product)));
    this.renderSpecifications(items);
  }
}

if (!customElements.get('yg-compare-toggle')) {
  customElements.define('yg-compare-toggle', YGCompareToggle);
}

if (!customElements.get('yg-comparison-bar')) {
  customElements.define('yg-comparison-bar', YGComparisonBar);
}

if (!customElements.get('yg-comparison-table')) {
  customElements.define('yg-comparison-table', YGComparisonTable);
}
